import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { RRule } from 'rrule';
import { formatDate } from '@frontend/date';
import { pendo } from '@frontend/tracking';
import { triggerScheduleCustomEvent } from './schedule-custom-events';
import { PendoTrackingEventName } from './trackingIds';

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

type ScheduleVersionState = 'v2' | 'v3';

const SCHEDULE_VERSION_STATE_LOCAL_STORAGE_KEY = 'weave.schedule-version-changed';

// Need to ignore endpoints which were used in both v2 and v3
const IGNORED_ENDPOINTS = ['ListSourceTenants'];

export const trackScheduleAPIVersionChange = (version: ScheduleVersionState, changeEventMetadata?: pendo.Metadata) => {
  const currentValue = localStorage.getItem(SCHEDULE_VERSION_STATE_LOCAL_STORAGE_KEY);

  const shouldIgnore = IGNORED_ENDPOINTS.includes((changeEventMetadata?.endpoint || '') as string);
  if (shouldIgnore) {
    // If the endpoint is in the ignored list, we do not track the change.
    return;
  }

  if (currentValue === version) {
    // If the current value is already the same as the version, we do not need to track it again.
    return;
  }

  if (currentValue !== version) {
    localStorage.setItem(SCHEDULE_VERSION_STATE_LOCAL_STORAGE_KEY, version);

    // Track the event only if the current value is not null or undefined
    if (currentValue) {
      pendo.track('schedule:api-version-change', { version, ...changeEventMetadata });
    }
  }
};

export const getUserTimezone = () => {
  try {
    return dayjs.tz.guess();
  } catch (error) {
    console.error('Error getting user timezone', error);
    return '';
  }
};

export const getTimezoneAbbreviation = (timezone: string) => {
  try {
    return dayjs().tz(timezone).format('z');
  } catch (error) {
    console.error('Error getting timezone abbreviation', error);
    return '';
  }
};

// Compare the UTC offset of two IANA timezones at the current time and returns true if the offsets are different.
export const isUserTimezoneOffsetDifferent = (userTimezone: string, compareTimezone: string): boolean => {
  const now = dayjs();
  try {
    return now.tz(userTimezone).utcOffset() !== now.tz(compareTimezone).utcOffset();
  } catch (error) {
    console.error('Error comparing timezones', error);
    return true;
  }
};

// Handle post user authentication, either through legacy or new sign-in method
export const handlePostLogin = () => {
  triggerScheduleCustomEvent('schedule:post-login');
};

/**
 * Checks if a calendar event overlaps with a query date range.
 *
 * @description
 * - Event datetimes (ISO 8601 format) - can span multiple days
 * - Query datetimes (single day range: start of day to end of day, ISO 8601 with timezone offset)
 * - Example: rangeStart: '2025-09-22T00:00:00-04:00', rangeEnd: '2025-09-22T23:59:59-04:00'
 * - Compares dates only (ignoring time) to determine overlap for more inclusive matching
 *
 * @param options - Configuration object containing event and query date ranges
 * @param options.startDate - Event start date/time (ISO 8601 format)
 * @param options.endDate - Event end date/time (ISO 8601 format)
 * @param options.rangeStart - Query range start (single day, start of day with timezone)
 * @param options.rangeEnd - Query range end (single day, end of day with timezone)
 * @returns True if the event overlaps with the query range, false otherwise
 */
export const isCalendarEventWithinRange = (options: {
  startDate?: string;
  endDate?: string;
  rangeStart?: string;
  rangeEnd?: string;
}): boolean => {
  const { startDate, endDate, rangeStart, rangeEnd } = options;
  if (!startDate || !endDate || !rangeStart || !rangeEnd) return false;

  // Compare dates only (ignoring time) for more inclusive overlap detection
  // This ensures that any event that falls on the same calendar date gets invalidated
  const eventStartDate = dayjs.utc(startDate).startOf('day');
  const eventEndDate = dayjs.utc(endDate).startOf('day');
  const queryStartDate = dayjs.utc(rangeStart).startOf('day');
  const queryEndDate = dayjs.utc(rangeEnd).startOf('day');

  const hasDateOverlap = eventStartDate.isSameOrBefore(queryEndDate) && eventEndDate.isSameOrAfter(queryStartDate);

  return hasDateOverlap;
};

export const trackPendoEvent = (
  eventName: PendoTrackingEventName,
  additionalData?: Record<string, string | number | boolean | string[]>
) => {
  pendo.track(eventName, additionalData);
};

/**
 * Extracts schedule information from an RRule string.
 *
 * @param {string} rrule - The recurrence rule string.
 * @param {number} [duration=0] - The duration in minutes for the event.
 * @param {'HH:mm' | 'hh:mm A'} timeFormat - The format for the start and end times.
 * @returns {IScheduleInfo} An object containing the start time, end time, and days of the week.
 */
export const getScheduleInfoFromRRule = (
  rrule: string,
  duration = 0,
  timeFormat: 'HH:mm' | 'hh:mm A'
): {
  startTime: string;
  endTime: string;
  daysOfWeek: number[];
} => {
  const rule = RRule.fromString(rrule);

  const dtStart = rule.options.dtstart;
  const startTime = formatDate(dtStart, timeFormat, true);
  const endTime = startTime ? dayjs(startTime, timeFormat).add(duration, 'm').format(timeFormat) : '';

  // rrule - MO: 0, TU: 1, WE: 2, TH: 3, FR: 4, SA: 5, SU: 6
  // dayjs - MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6, SU: 0,

  // Convert RRule weekdays to dayjs weekdays
  const daysOfWeek = rule.options.byweekday.map((weekday: number) => (weekday === 6 ? 0 : weekday + 1));

  return {
    startTime,
    endTime,
    daysOfWeek,
  };
};
